import { LessonDetail } from "@/components/books/LessonDetail";
import type { BookLesson, BookSeries } from "@/components/books/bookLesson.types";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { LoadingSpinner } from "@ai-teach/ui-web";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { useMemo, useState } from "react";

const ALL = "__all__";

const BOOK_SERIES = gql`
  query BookSeries {
    bookSeries {
      slug
      lessonCount
      levels { id name }
      components { id name }
    }
  }
`;

const BOOK_LESSONS = gql`
  query BookLessons($seriesSlug: String!) {
    bookLessons(seriesSlug: $seriesSlug) {
      id
      levelId
      levelName
      componentId
      componentName
      lesson
      printedLabel
      pageLineCount
      rows {
        type
        position
        text
        partOfSpeech
        example
        lessonContext
        description
        grammarNote
        note
        subList
        tables {
          heading
          rows { kind text }
        }
      }
      pageLines { kind section text }
    }
  }
`;

interface SeriesData {
  bookSeries: BookSeries[];
}

interface LessonsData {
  bookLessons: BookLesson[];
}

interface Option {
  value: string;
  label: string;
}

function Picker({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Option[];
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-[10rem] rounded-lg border border-gray-200 dark:border-gray-800 bg-background px-2.5 py-2 text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30"
      >
        {options.map((one) => (
          <option key={one.value} value={one.value}>
            {one.label}
          </option>
        ))}
      </select>
    </label>
  );
}

// Every state below the toolbar fills the remaining height, so a spinner or an empty state sits
// centred in the page rather than pinned under the title.
function Centred({ children }: { children: React.ReactNode }) {
  return <div className="flex-1 min-h-0 flex items-center justify-center">{children}</div>;
}

/** Local rather than ui-web's EmptyState, which is inline-styled for a light page only. */
function Notice({
  icon,
  title,
  description,
}: {
  icon: string;
  title: string;
  description: string;
}) {
  return (
    <div className="max-w-md rounded-2xl border border-gray-200 dark:border-gray-800 px-6 py-8 text-center">
      <div className="text-3xl">{icon}</div>
      <h2 className="card-title mt-2">{title}</h2>
      <p className="body-sm mt-1 text-gray-500 dark:text-gray-400">{description}</p>
    </div>
  );
}

/** Lessons keep the book's own order; the level headings follow from it rather than sorting it. */
function groupByLevel(lessons: BookLesson[]) {
  const groups = new Map<string, BookLesson[]>();
  for (const one of lessons) {
    const group = groups.get(one.levelName);
    if (group) group.push(one);
    else groups.set(one.levelName, [one]);
  }
  return [...groups];
}

function LessonList({
  lessons,
  selectedId,
  onSelect,
}: {
  lessons: BookLesson[];
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const groups = groupByLevel(lessons);
  const showHeadings = groups.length > 1;

  return (
    <aside className="min-h-0 flex flex-col overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-800">
      <div className="shrink-0 flex items-baseline gap-2 border-b border-gray-200 dark:border-gray-800 px-4 py-3">
        <h2 className="text-[11px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Lessons
        </h2>
        <span className="text-[11px] tabular-nums text-gray-400">{lessons.length}</span>
      </div>
      <div className="min-h-0 overflow-y-auto p-2">
        {groups.map(([levelName, group]) => (
          <div key={levelName}>
            {showHeadings && (
              // The band is full-bleed against the card edge, so `-mx-2` undoes the list padding.
              <h3 className="sticky top-0 z-10 -mx-2 mb-1 border-y border-primary-200 dark:border-primary-700 bg-primary-50 dark:bg-primary-900 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-primary-700 dark:text-primary-100">
                {levelName}
              </h3>
            )}
            {group.map((one) => {
              const selected = one.id === selectedId;
              return (
                <button
                  key={one.id}
                  type="button"
                  onClick={() => onSelect(one.id)}
                  className={`w-full rounded-lg px-2.5 py-1.5 text-left transition-colors ${
                    selected
                      ? "bg-primary-500/10 text-primary-700 dark:text-primary-200 ring-1 ring-inset ring-primary-500/50"
                      : "text-gray-700 dark:text-gray-300 hover:bg-primary-500/5"
                  }`}
                >
                  <span className="block text-sm font-semibold">{one.printedLabel}</span>
                  <span className="block text-xs opacity-70 truncate">{one.componentName}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </aside>
  );
}

/**
 * Books — imported book content, lesson by lesson. Read-only; rows are loaded by
 * `import:book-lessons` in b2b-api from a book-core response.
 */
export function BooksPage() {
  const [seriesSlug, setSeriesSlug] = useState<string | null>(null);
  const [levelId, setLevelId] = useState(ALL);
  const [componentId, setComponentId] = useState(ALL);
  const [selected, setSelected] = useState<string | null>(null);

  const seriesQuery = useQuery<SeriesData>(BOOK_SERIES, { fetchPolicy: "cache-and-network" });
  const allSeries = useMemo(() => seriesQuery.data?.bookSeries ?? [], [seriesQuery.data]);
  const series = allSeries.find((one) => one.slug === seriesSlug) ?? allSeries[0] ?? null;

  const lessonsQuery = useQuery<LessonsData>(BOOK_LESSONS, {
    variables: { seriesSlug: series?.slug ?? "" },
    skip: !series,
    fetchPolicy: "cache-first",
  });

  const lessons = useMemo(() => lessonsQuery.data?.bookLessons ?? [], [lessonsQuery.data]);
  const shown = useMemo(
    () =>
      lessons.filter(
        (one) =>
          (levelId === ALL || one.levelId === levelId) &&
          (componentId === ALL || one.componentId === componentId)
      ),
    [lessons, levelId, componentId]
  );
  const lesson = shown.find((one) => one.id === selected) ?? shown[0] ?? null;

  const body = () => {
    if (seriesQuery.loading && allSeries.length === 0) {
      return (
        <Centred>
          <LoadingSpinner />
        </Centred>
      );
    }

    if (seriesQuery.error) {
      return (
        <Centred>
          <Notice icon="⚠️" title="Couldn't load books" description={seriesQuery.error.message} />
        </Centred>
      );
    }

    if (!series) {
      return (
        <Centred>
          <Notice
            icon="📚"
            title="No books imported yet"
            description="Run `pnpm import:book-lessons <file.json>` in apps/b2b-api to import a book-core response."
          />
        </Centred>
      );
    }

    return (
      <div className="flex-1 min-h-0 flex flex-col gap-5">
        <div className="shrink-0 flex flex-wrap items-end gap-4 border-b border-gray-200 dark:border-gray-800 pb-4">
          <Picker
            label="Book"
            value={series.slug}
            onChange={(slug) => {
              setSeriesSlug(slug);
              // The filters name ids of the book being left; keeping them would show nothing.
              setLevelId(ALL);
              setComponentId(ALL);
              setSelected(null);
            }}
            options={allSeries.map((one) => ({
              value: one.slug,
              label: `${one.slug} (${one.lessonCount})`,
            }))}
          />
          {/* Derived from the imported lessons, in the book's own order. A component the book has
              but which was never imported is therefore absent rather than shown as empty. */}
          <Picker
            label="Level"
            value={levelId}
            onChange={setLevelId}
            options={[
              { value: ALL, label: "All levels" },
              ...series.levels.map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
          <Picker
            label="Book type"
            value={componentId}
            onChange={setComponentId}
            options={[
              { value: ALL, label: "All book types" },
              ...series.components.map((one) => ({ value: one.id, label: one.name })),
            ]}
          />
          <p className="ml-auto pb-2 text-xs tabular-nums text-gray-500 dark:text-gray-400">
            <span className="font-bold text-gray-900 dark:text-gray-200">{shown.length}</span> of{" "}
            {lessons.length} lessons
          </p>
        </div>

        {lessonsQuery.loading && lessons.length === 0 ? (
          <Centred>
            <LoadingSpinner />
          </Centred>
        ) : shown.length === 0 ? (
          <Centred>
            <p className="text-sm text-gray-500">No lessons for this level and book type.</p>
          </Centred>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-[260px_1fr] gap-6">
            <LessonList lessons={shown} selectedId={lesson?.id} onSelect={setSelected} />
            <section className="min-h-0 overflow-y-auto pr-1">
              {lesson && <LessonDetail lesson={lesson} />}
            </section>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-screen">
      <MainSidebar />
      {/* min-h-0 so the scrolling descendants below can shrink instead of growing the page. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-5 p-6">
        <div className="shrink-0">
          <h1 className="h3">Books</h1>
          <p className="body-sm text-gray-500 dark:text-gray-400">
            Imported book content, lesson by lesson — what the book says, as against what it
            teaches.
          </p>
        </div>
        {body()}
      </div>
    </div>
  );
}
